// Saving a star chart: profile birth fields, the star_chart module row, and
// the chart tags. Shared by the Star Chart module and first-run onboarding so
// both write the exact same shape.

import { saveModuleCompletion, saveTags, updateProfile } from "./db";
import { computeNatal, toLegacyChart } from "./astro/natal";

const calculateLocalChart = (bd) => toLegacyChart(computeNatal(bd));
import { calculateVedicChart } from "../components/star-charts/vedicMath";
import { collectChineseTags } from "../components/star-charts/starChartChineseTags";
import { collectMayanTags } from "../components/star-charts/starChartMayanTags";
import { collectVedicTags } from "../components/star-charts/starChartVedicTags";

/** The payload StarChartModule saves after a first chart. */
export function buildStarChartPayload(birthData) {
  const chartData = calculateLocalChart(birthData);
  return {
    birthData,
    chartData,
    sunSign: chartData.placements.sun?.sign,
    vedicData: calculateVedicChart(birthData),
    // Each tradition is marked viewed when first opened; none starts ahead.
    viewed: {},
  };
}

export async function persistStarChart(userId, data) {
  // Save birth data to profiles table.
  // birth_time is a Postgres TIME column — needs HH:MM:SS in 24h, not "11:30 AM".
  // birth_location is split across four columns, not one.
  if (data.birthData) {
    const bd = data.birthData;
    let birthTime24 = null;
    if (bd.birthTime) {
      let h = bd.birthTime.hour;
      if (bd.birthTime.period === "PM" && h !== 12) h += 12;
      if (bd.birthTime.period === "AM" && h === 12) h = 0;
      birthTime24 = `${String(h).padStart(2, "0")}:${String(bd.birthTime.minute).padStart(2, "0")}:00`;
    }
    await updateProfile(userId, {
      birth_date: bd.birthDate
        ? `${bd.birthDate.year}-${String(bd.birthDate.month).padStart(2, "0")}-${String(bd.birthDate.day).padStart(2, "0")}`
        : null,
      birth_time: birthTime24,
      birth_location_city: bd.birthLocation?.city ?? null,
      birth_location_lat: bd.birthLocation?.lat ?? null,
      birth_location_lng: bd.birthLocation?.lng ?? null,
      birth_location_timezone: bd.birthLocation?.timezone ?? null,
    });
  }
  // Save chart completion
  await saveModuleCompletion(userId, "star_chart", "western", data);
  // Save star chart tags
  const placements = data.chartData?.placements;
  if (placements) {
    const tags = [];
    if (placements.sun?.sign) tags.push({ tag_key: "sun_sign", tag_value: placements.sun.sign, tag_type: "offers", source_module: "star_chart" });
    if (placements.moon?.sign) tags.push({ tag_key: "moon_sign", tag_value: placements.moon.sign, tag_type: "offers", source_module: "star_chart" });
    if (placements.rising?.sign) tags.push({ tag_key: "rising_sign", tag_value: placements.rising.sign, tag_type: "offers", source_module: "star_chart" });
    if (placements.venus?.sign) tags.push({ tag_key: "venus_sign", tag_value: placements.venus.sign, tag_type: "offers", source_module: "star_chart" });
    if (placements.mars?.sign) tags.push({ tag_key: "mars_sign", tag_value: placements.mars.sign, tag_type: "offers", source_module: "star_chart" });
    // Need-side tags
    if (placements.descendant?.sign) tags.push({ tag_key: "descendant_sign", tag_value: placements.descendant.sign, tag_type: "needs", source_module: "star_chart" });
    if (placements.venus?.sign) tags.push({ tag_key: "venus_need", tag_value: placements.venus.sign, tag_type: "needs", source_module: "star_chart" });
    if (placements.northNode?.sign) tags.push({ tag_key: "north_node", tag_value: placements.northNode.sign, tag_type: "needs", source_module: "star_chart" });
    if (tags.length > 0) await saveTags(userId, tags);
  }
  // Chinese zodiac tags — emitted whenever the Chinese sub-chart is in the
  // payload (fires when the user views the Chinese system). The collector
  // dedups by tag_key|tag_type; saveTags upserts so re-fires are harmless.
  if (data.chineseData) {
    const chineseTags = collectChineseTags(data.chineseData);
    if (chineseTags.length) await saveTags(userId, chineseTags);
  }
  // Mayan Tzolk'in tags — emitted when the Mayan sub-chart is in the payload.
  if (data.mayanData) {
    const mayanTags = collectMayanTags(data.mayanData);
    if (mayanTags.length) await saveTags(userId, mayanTags);
  }
  // Vedic tags — keyed off chart structure, independent of the API
  // interpretation. Emitted as soon as the Vedic chart is calculated.
  if (data.vedicData) {
    const vedicTags = collectVedicTags(data.vedicData);
    if (vedicTags.length) await saveTags(userId, vedicTags);
  }
}
